


import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Heart, Sparkles, Droplets } from "lucide-react";

export default function AboutSection() {
  const features = [
    {
      icon: <Sparkles size={30} />,
      title: "Ultra Thin",
      desc: "Designed with advanced absorbent layers that feel feather-light while delivering maximum protection throughout the day.",
    },
    {
      icon: <Heart size={30} />,
      title: "Skin Friendly",
      desc: "Soft breathable materials that are gentle on sensitive skin and help reduce irritation.",
    },
    {
      icon: <Droplets size={30} />,
      title: "Leak Protection",
      desc: "360° leak-lock technology provides superior absorption and confidence during heavy flow days.",
    },
    {
      icon: <ShieldCheck size={30} />,
      title: "Safe & Hygienic",
      desc: "Dermatologically tested products crafted for comfort, freshness and complete hygiene.",
    },
  ];

  return (
    <section
      className="
        relative
        overflow-hidden
        bg-gradient-to-b
        from-[#fff8fc]
        via-white
        to-[#f8fbff]
      "
    >
      {/* ================= HERO SECTION ================= */}

      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 lg:pt-28 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* ================= LEFT CONTENT ================= */}

          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {/* TAG */}

            <div
              className="
                inline-flex
                items-center
                px-5
                py-2
                rounded-full
                bg-pink-100
                text-pink-600
                text-xs
                md:text-sm
                font-semibold
                tracking-[2px]
                uppercase
                mb-6
              "
            >
              About Confidays
            </div>

            {/* HEADING */}

            <h2
              className="
                text-[38px]
                sm:text-[52px]
                lg:text-[68px]

                leading-[0.98]

                font-bold

                text-[#1A237E]

                mb-8
              "
            >
              Comfort,
              <br />
              Confidence &<span className="text-pink-500"> Care</span>
            </h2>

            {/* TEXT */}

            <div className="space-y-5">
              <p
                className="
                  text-gray-600
                  leading-[1.9]
                  text-[15px]
                  md:text-[17px]
                "
              >
                At Confidays, we believe every woman deserves complete comfort,
                confidence and protection during every phase of life.
              </p>

              <p
                className="
                  text-gray-600
                  leading-[1.9]
                  text-[15px]
                  md:text-[17px]
                "
              >
                Our premium sanitary and wellness products are thoughtfully
                crafted using ultra-soft, skin-friendly materials combined with
                advanced absorbent technology.
              </p>

              <p
                className="
                  text-gray-600
                  leading-[1.9]
                  text-[15px]
                  md:text-[17px]
                "
              >
                From everyday hygiene essentials to advanced protection,
                Confidays empowers women with reliable care and confidence every
                single day.
              </p>
            </div>

            {/* BUTTONS */}

            <div className="flex flex-wrap gap-4 mt-10">
              <a
                href="/products"
                className="
                  px-8
                  py-3.5

                  rounded-full

                  bg-gradient-to-r
                  from-pink-500
                  to-pink-400

                  text-white
                  font-semibold

                  shadow-lg

                  transition-all
                  duration-300

                  hover:scale-105
                "
              >
                Explore Products
              </a>

              <a
                href="/contact"
                className="
                  px-8
                  py-3.5

                  rounded-full

                  border-2
                  border-[#1A237E]

                  text-[#1A237E]
                  font-semibold

                  transition-all
                  duration-300

                  hover:bg-[#1A237E]
                  hover:text-white
                "
              >
                Contact Us
              </a>
            </div>
          </motion.div>

          {/* ================= RIGHT IMAGE ================= */}

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="relative flex justify-center"
          >
            {/* GLOW */}

            <div
              className="
                absolute
                w-[380px]
                h-[380px]

                bg-pink-200/40

                rounded-full

                blur-3xl
              "
            />

            {/* MAIN IMAGE */}

            <img
              loading="lazy"
              src="/about-us.png"
              alt="Confidays Care"
              className="
                relative
                z-10

                w-[280px]
                sm:w-[360px]
                lg:w-[430px]

                rounded-[34px]

                shadow-[0_20px_60px_rgba(236,72,153,0.15)]

                object-cover
              "
            />

            {/* FLOATING CARD */}

            <div
              className="
                absolute
                -bottom-6
                -left-4

                bg-white/90
                backdrop-blur-lg

                rounded-3xl

                px-5
                py-4

                shadow-xl

                border
                border-pink-100

                z-20
              "
            >
              <div className="flex items-center gap-3">
                <div
                  className="
                    w-12
                    h-12

                    rounded-2xl

                    bg-pink-100

                    flex
                    items-center
                    justify-center

                    text-pink-500
                  "
                >
                  <ShieldCheck size={24} />
                </div>

                <div>
                  <h4 className="font-bold text-[#1A237E] text-base">
                    100% Safe
                  </h4>

                  <p className="text-gray-500 text-xs">
                    Dermatologically Tested
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ================= HAPPY CUSTOMERS BANNER ================= */}

      <div className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4">
          <motion.img
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            src="/happy-customers-banner.png"
            alt="Happy Customers"
            className="
              w-full

              rounded-[32px]

              object-cover

              shadow-[0_20px_60px_rgba(236,72,153,0.12)]
            "
          />
        </div>
      </div>

      {/* ================= FEATURES SECTION ================= */}

      <div
        className="
          relative

          bg-[#fff7fb]

          border-y
          border-pink-100
        "
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
          {/* HEADING */}

          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h3
              className="
                text-[32px]
                md:text-[46px]

                font-bold

                text-[#1A237E]

                mb-4
              "
            >
              Why Women Love
              <span className="text-pink-500"> Confidays</span>
            </h3>

            <p
              className="
                max-w-3xl
                mx-auto

                text-gray-500

                leading-[1.8]

                text-[15px]
                md:text-[17px]
              "
            >
              Experience premium hygiene care crafted with advanced absorption
              technology, ultra-soft comfort and complete leak protection for
              everyday confidence.
            </p>
          </motion.div>

          {/* FEATURES GRID */}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {features.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.1,
                }}
                viewport={{ once: true }}
                className="
                  group

                  bg-white

                  rounded-[30px]

                  p-7

                  border
                  border-pink-100

                  shadow-[0_10px_35px_rgba(236,72,153,0.06)]

                  transition-all
                  duration-300

                  hover:-translate-y-2
                  hover:shadow-[0_20px_50px_rgba(236,72,153,0.12)]
                "
              >
                {/* ICON */}

                <div
                  className="
                    w-18
                    h-18

                    rounded-full

                    bg-pink-100

                    flex
                    items-center
                    justify-center

                    text-pink-500

                    mb-6

                    group-hover:scale-110

                    transition
                  "
                >
                  {item.icon}
                </div>

                {/* TITLE */}

                <h4
                  className="
                    text-[22px]
                    font-bold

                    text-[#1A237E]

                    mb-4
                  "
                >
                  {item.title}
                </h4>

                {/* DESCRIPTION */}

                <p
                  className="
                    text-gray-500

                    leading-[1.9]

                    text-[15px]
                  "
                >
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}